import { Code, ConnectError } from "@connectrpc/connect";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@viajero/shared-ui/analytics";
import { Carousel } from "@/components/Carousel";
import { QueryStateFallback } from "@/components/QueryStateFallback";
import { useCheckAvailability } from "@/features/courses/api/useCheckAvailability";
import { useGetProductDetail } from "@/features/courses/api/useGetProductDetail";
import {
  AvailabilityCheckModal,
  type CourseAvailabilityStatus,
} from "@/features/courses/components/AvailabilityCheckModal";
import { CourseBookingForm } from "@/features/courses/components/CourseBookingForm";
import { CourseContentSections } from "@/features/courses/components/CourseContentSections";
import { calculateTotalPrice } from "@/lib/calculateTotalPrice";
import { useCourseBookingContext } from "@/lib/courseBookingContext";
import { isValidId } from "@/lib/id";
import { isBookingSelectionValid } from "@/lib/isBookingSelectionValid";
import { parsePartySize } from "@/lib/parsePartySize";
import { resolvePartySizeRange, selectableMaxQuantity } from "@/lib/partySizeRange";
import { toDateInputValue } from "@/lib/toDateInputValue";
import { isSelectableTime, resolveTurnStartTimes } from "@/lib/turnStartTimes";
import { colors } from "@/theme/colors";

type Props = {
  caseToken: string;
  shopId: string;
  productId: string;
  // 案件の予約希望日時・人数 (GetReservationContext の preferred_date / preferred_time /
  // preferred_party_size)。抽出できていない場合は空文字。
  preferredDate: string;
  preferredTime: string;
  preferredPartySize: string;
  onReserve: () => void;
  onBackToShop: () => void;
};

export function CourseDetailView({
  caseToken,
  shopId,
  productId,
  preferredDate,
  preferredTime,
  preferredPartySize,
  onReserve,
  onBackToShop,
}: Props) {
  const { t } = useTranslation();
  const query = useGetProductDetail(shopId, productId, Boolean(caseToken));
  const { selection, setSelection } = useCourseBookingContext();
  const product = query.data?.product;
  // 「空席確認」押下時のみ実行する空席確認。押下のたびに最新の空席状況を取り直す。
  const availability = useCheckAvailability();
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  // 案件の予約希望日時・人数を利用日時と人数選択のデフォルト値として設定する。
  // 人数の割当先 (価格種別) と選択上限は商品詳細の応答に含まれるため、商品取得後に日時と
  // まとめて設定する (日時だけ先に入ると下のガードに引っかかり人数が設定されなくなる)。
  // ユーザーが既に利用日か時間を入力済みの場合、または人数を操作済みの場合は上書きしない。
  useEffect(() => {
    if (!product) return;
    if (selection.date || selection.time) return;
    // 人数は 0 を選んだ場合もキーが入るため、キーの有無で「触ったかどうか」を判定する
    // (0 を「未入力」とみなすと、0 に戻した直後に再シードで上書きしてしまう)。
    if (Object.keys(selection.quantities).length > 0) return;
    // 予約希望人数は合計人数のみで価格種別ごとの内訳を持たないため、先頭の価格種別に割り当てる。
    // 選択肢の上限を超える人数は Select が空表示になってしまうため設定しない。
    const partySize = parsePartySize(preferredPartySize);
    const priceTypeId = product.prices[0]?.priceTypeId;
    const maxQuantity = selectableMaxQuantity(resolvePartySizeRange(product));
    const quantities =
      partySize !== null && priceTypeId !== undefined && partySize <= maxQuantity
        ? { [priceTypeId.toString()]: partySize }
        : null;
    // 回転設定 on の店舗では開始時間以外は選べないため、希望時間が一致しなければ未選択にする
    // (先頭の開始時間を勝手に選ぶと、希望と異なる時間のまま気づかず進んでしまう)。
    const time = isSelectableTime(product, preferredTime) ? preferredTime : "";
    // 設定できる初期値が一つも無ければ setSelection を呼ばない。同値でも新しい selection
    // オブジェクトが state に入り、この effect が再実行され続けてしまうため。
    if (!preferredDate && !time && !quantities) return;
    setSelection({
      ...selection,
      date: preferredDate,
      time,
      quantities: quantities ?? selection.quantities,
    });
  }, [product, preferredDate, preferredTime, preferredPartySize, selection, setSelection]);

  const isNotFound =
    !isValidId(shopId) ||
    !isValidId(productId) ||
    (query.error instanceof ConnectError && query.error.code === Code.NotFound);

  if (query.isLoading || isNotFound || query.isError || !product) {
    return (
      <QueryStateFallback
        isLoading={query.isLoading}
        isNotFound={isNotFound}
        isError={query.isError || !product}
        notFound={{
          title: t("courseDetail.notFound"),
          description: t("courseDetail.notFoundDescription"),
          actions: [{ label: t("errorPage.backToShop"), onClick: onBackToShop }],
        }}
        error={{
          title: t("courseDetail.error"),
          description: t("courseDetail.errorDescription"),
          actions: [
            { label: t("errorPage.reload"), onClick: () => void query.refetch() },
            { label: t("errorPage.backToShop"), onClick: onBackToShop, variant: "outlined" },
          ],
        }}
      />
    );
  }

  // CourseContentSections が何か描画するか (全項目が空なら何も描画しない)。
  // 直後にコンテンツが無いときはタイトル直後の divider を出さず separator の二重表示を防ぐ。
  const hasContent = Boolean(
    product.description ||
      product.includedInCost ||
      product.notIncludedInCost ||
      product.meetingPlaceAddress,
  );
  // 利用日カレンダーで過去日を選択できないようにする最小選択日 (本日)。
  const minDate = toDateInputValue(new Date());
  const totalPrice = calculateTotalPrice(product.prices, selection.quantities);
  const canProceed = isBookingSelectionValid(selection, product, minDate);
  // 人数が実効範囲を満たさないときに理由を示す。上限は選択肢を絞っているが、価格種別ごとの
  // セレクトがそれぞれ上限まで選べるため合計では超過し得る (大人 3 + 小人 1 など)。
  const partySizeRange = resolvePartySizeRange(product);
  const totalQuantity = Object.values(selection.quantities).reduce((sum, n) => sum + n, 0);
  const partySizeNotice = partySizeRange.unavailable
    ? t("courseDetail.partySizeUnavailable")
    : totalQuantity > 0 && totalQuantity < partySizeRange.min
      ? t("courseDetail.partySizeBelowMin", { min: partySizeRange.min })
      : partySizeRange.max > 0 && totalQuantity > partySizeRange.max
        ? t("courseDetail.partySizeAboveMax", { max: partySizeRange.max })
        : "";

  // 空席確認は選択中の利用日・時間・合計人数で行う。ボタンは canProceed (日時・人数が
  // 有効) のときだけ押せるため、ここでの値は常に確認リクエストとして成立する。
  const runAvailabilityCheck = () => {
    availability.mutate(
      {
        shopId: BigInt(shopId),
        date: selection.date,
        time: selection.time,
        partySize: totalQuantity,
      },
      {
        // 「空席がありません」モーダルの表示を記録する (JPTP_CANSEI-1400)。エラー
        // (Bot 操作の失敗・判定不能) は「空席なし」と区別するため送らない。
        onSuccess: (res) => {
          if (!res.available) trackEvent("view_no_vacancy", visitEventParams);
        },
      },
    );
  };
  // 直前の結果が残っている状態で押し直したときに古い結果を見せないよう、実行中を最優先で判定する。
  const availabilityStatus: CourseAvailabilityStatus = availability.isError
    ? "error"
    : availability.isSuccess
      ? availability.data.available
        ? "available"
        : "unavailable"
      : "checking";
  // 空席確認と予約情報入力への遷移で同じ条件を送る。人数は価格種別ごとの内訳を持つため合計にする。
  const visitEventParams = {
    visit_date: selection.date,
    visit_time: selection.time,
    party_size: totalQuantity,
  };

  return (
    <Box sx={{ pb: { xs: 4, sm: 6 }, maxWidth: 640, width: "100%", mx: "auto" }}>
      <Carousel images={product.imageUrl} />

      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: { xs: 2, sm: 3 } }}>
        <Typography
          component="h1"
          variant="h5"
          sx={{ fontWeight: 700, color: colors.text, mb: 2, px: 1.5 }}
        >
          {product.name}
        </Typography>

        {hasContent && <Divider sx={{ mb: 2 }} />}

        <Box sx={{ px: 1.5 }}>
          <CourseContentSections
            description={product.description}
            includedInCost={product.includedInCost}
            notIncludedInCost={product.notIncludedInCost}
            meetingPlaceAddress={product.meetingPlaceAddress}
          />
        </Box>

        {product.note && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: colors.text, mb: 1, px: 1.5 }}>
              {t("courseDetail.notes")}
            </Typography>
            <Typography
              sx={{ color: colors.textBody, fontSize: 13, whiteSpace: "pre-line", px: 1.5 }}
            >
              {product.note}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ px: 1.5 }}>
          <CourseBookingForm
            prices={product.prices}
            date={selection.date}
            minDate={minDate}
            time={selection.time}
            turnStartTimes={resolveTurnStartTimes(product)}
            quantities={selection.quantities}
            partySizeRange={partySizeRange}
            totalPrice={totalPrice}
            onDateChange={(date) => setSelection({ ...selection, date })}
            onTimeChange={(time) => setSelection({ ...selection, time })}
            onQuantityChange={(priceTypeId, quantity) =>
              setSelection({
                ...selection,
                quantities: { ...selection.quantities, [priceTypeId]: quantity },
              })
            }
          />
        </Box>

        {partySizeNotice && (
          <Typography sx={{ color: colors.textSub, fontSize: 13, px: 1.5, mt: 2 }}>
            {partySizeNotice}
          </Typography>
        )}

        <Box sx={{ px: 1.5, mt: 3, display: "flex", justifyContent: { md: "flex-end" } }}>
          <Button
            variant="contained"
            disabled={!canProceed}
            onClick={() => {
              trackEvent("click_check_availability", visitEventParams);
              setAvailabilityModalOpen(true);
              runAvailabilityCheck();
            }}
            sx={{ width: { xs: "100%", md: "auto" }, minHeight: 48 }}
          >
            {t("courseDetail.checkAvailability")}
          </Button>
        </Box>
      </Box>

      <AvailabilityCheckModal
        open={availabilityModalOpen}
        status={availabilityStatus}
        onProceed={() => {
          trackEvent("click_proceed_booking", visitEventParams);
          onReserve();
        }}
        onClose={() => setAvailabilityModalOpen(false)}
      />
    </Box>
  );
}
